<template>
  <div class="fixed bottom-safe pb-4 pr-4 right-0 text-n-0 pointer-events-auto flex sm:flex-col">
    <div class="flex sm:flex-col rounded overflow-hidden">
      <l-tooltip trigger="click" placement="auto">
        <div
          class="p-3 transition-colors cursor-pointer"
          :class="{
            'bg-v-300 pointer-events-none': model === 'time',
            'bg-n-700': model !== 'time'
          }"
          @click="emitModeChange('time')"
        >
          <l-icon name="time" size="medium" />
        </div>
        <template #content>
          {{ t('floorplan.time') }}
        </template>
      </l-tooltip>
      <l-tooltip trigger="click" placement="auto">
        <div
          class="p-3 transition-colors cursor-pointer"
          :class="{
            'bg-v-300 pointer-events-none': model === 'diners',
            'bg-n-700': model !== 'diners'
          }"
          @click="emitModeChange('diners')"
        >
          <l-icon name="diners" size="medium" />
        </div>
        <template #content>
          {{ t('floorplan.diners') }}
        </template>
      </l-tooltip>
      <l-tooltip trigger="click" placement="auto">
        <div
          class="p-3 transition-colors cursor-pointer"
          :class="{
            'bg-v-300 pointer-events-none': model === 'amount',
            'bg-n-700': model !== 'amount'
          }"
          @click="emitModeChange('amount')"
        >
          <l-icon :name="currencyIcon" size="medium" />
        </div>
        <template #content>
          {{ t('floorplan.amount') }}
        </template>
      </l-tooltip>
      <l-tooltip v-if="hasReservationSubscription" trigger="click" placement="auto">
        <div
          class="p-3 transition-colors cursor-pointer"
          :class="{
            'bg-v-300 pointer-events-none': model === 'reservation',
            'bg-n-700': model !== 'reservation'
          }"
          @click="emitModeChange('reservation')"
        >
          <l-icon name="calendar" size="medium" />
        </div>
        <template #content>
          {{ t('floorplan.reservation') }}
        </template>
      </l-tooltip>
    </div>

    <l-tooltip trigger="click" placement="auto-end">
      <div class="p-3 rounded ml-4 sm:mt-4 sm:ml-0 bg-n-700 cursor-pointer">
        <l-icon name="status" size="medium" />
      </div>
      <template #content>
        <div class="flex flex-col bg-n-600 px-4 py-4">
          <div class="flex items-center text-n-200 mb-4">
            <div class="w-4 h-4 mr-2 bg-n-0 rounded-full" />
            {{ t('floorplan.free') }}
          </div>
          <div class="flex items-center text-n-200 mb-4">
            <div class="w-4 h-4 mr-2 rounded-full bg-[#3399FF]" />
            {{ t('floorplan.occupied') }}
          </div>
          <div class="flex items-center text-n-200 mb-4">
            <div class="w-4 h-4 mr-2 rounded-full bg-[#FF4D4D]" />
            {{ t('floorplan.to-pay') }}
          </div>
          <div class="flex items-center text-n-200">
            <div class="w-4 h-4 mr-2 rounded-full bg-[#FFD442]" />
            {{ t('floorplan.reserved') }}
          </div>
          <template v-if="config.enableKitchenOrders">
            <div class="flex items-center text-n-200 mt-4">
              <div class="w-4 h-4 mr-2 bg-[#FFD442] rounded-full border-2 border-n-0"></div>
              {{ t('floorplan.not-sent-to-kitchen') }}
            </div>
          </template>
          <div class="flex items-center text-n-200 mt-4">
            <div class="w-4 h-4 mr-2 bg-[#9C6DFE] rounded-full border-2 border-n-0"></div>
            {{ t('floorplan.multiple-tabs') }}
          </div>
        </div>
      </template>
    </l-tooltip>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { LIcon, LTooltip, useTracker } from '@last/core-ui/paprika'

import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'

type ModeType = 'time' | 'diners' | 'amount' | 'reservation'

const model = defineModel<ModeType>({
  default: 'time'
})

const tracker = useTracker()
const { t } = useI18n()

const configStore = useConfigStore()
const authStore = useAuthStore()

const { config, currencyIcon } = storeToRefs(configStore)
const { hasReservationSubscription } = storeToRefs(authStore)

function emitModeChange(mode: ModeType) {
  tracker.track(`floorplanMode:${mode}`)
  model.value = mode
}
</script>
